/**
 * Admin · Users.
 *
 * Paginated table of users with last-seen times. Search by email/username,
 * sort by last seen / created / email, page through with offset/limit.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';

const PAGE_SIZES = [25, 50, 100];

const state = {
  q: '',
  sort: 'last_seen',
  order: 'desc',
  pageSize: 25,
  offset: 0,
  total: 0,
  hasMore: false,
};

export async function renderAdminUsers(root) {
  const me = auth.getMe();
  if (!me?.is_admin) {
    root.innerHTML = `
      <div class="appbar"><div class="appbar-title">Users (admin)</div></div>
      <div class="pad"><div class="card" style="color:var(--color-danger)">Admins only.</div></div>
    `;
    return;
  }

  root.innerHTML = `
    <div class="appbar">
      <a href="/admin" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Users</div>
      <div class="appbar-spacer"></div>
      <span class="status-pill bad">ADMIN</span>
    </div>
    <div class="pad" id="users-page">
      <div class="card" style="padding:12px;">
        <form id="users-filter" class="row" style="gap:8px;flex-wrap:wrap;align-items:flex-end;">
          <label style="flex:1;min-width:180px;">
            <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:3px;">Search</div>
            <input type="search" id="filter-q" placeholder="email or username" value="${escape(state.q)}" autocomplete="off" />
          </label>
          <label>
            <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:3px;">Sort</div>
            <select id="filter-sort">
              <option value="last_seen" ${state.sort === 'last_seen' ? 'selected' : ''}>Last seen</option>
              <option value="created" ${state.sort === 'created' ? 'selected' : ''}>Joined</option>
              <option value="email" ${state.sort === 'email' ? 'selected' : ''}>Email</option>
            </select>
          </label>
          <label>
            <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:3px;">Order</div>
            <select id="filter-order">
              <option value="desc" ${state.order === 'desc' ? 'selected' : ''}>Desc</option>
              <option value="asc" ${state.order === 'asc' ? 'selected' : ''}>Asc</option>
            </select>
          </label>
          <label>
            <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:3px;">Per page</div>
            <select id="filter-size">
              ${PAGE_SIZES.map((n) => `<option value="${n}" ${state.pageSize === n ? 'selected' : ''}>${n}</option>`).join('')}
            </select>
          </label>
        </form>
      </div>

      <div class="card" style="padding:12px;margin-top:10px;" id="users-card">
        <div id="users-summary" class="muted" style="font-size:11px;margin-bottom:6px;"></div>
        <div id="users-list"><div class="muted">Loading…</div></div>
        <div id="users-pager" class="row" style="justify-content:space-between;margin-top:10px;"></div>
      </div>
    </div>
  `;

  document.getElementById('filter-q').addEventListener('input', debounce((e) => {
    state.q = e.target.value;
    state.offset = 0;
    void loadUsers();
  }, 250));
  document.getElementById('filter-sort').addEventListener('change', (e) => {
    state.sort = e.target.value;
    state.offset = 0;
    void loadUsers();
  });
  document.getElementById('filter-order').addEventListener('change', (e) => {
    state.order = e.target.value;
    state.offset = 0;
    void loadUsers();
  });
  document.getElementById('filter-size').addEventListener('change', (e) => {
    state.pageSize = Number(e.target.value);
    state.offset = 0;
    void loadUsers();
  });

  await loadUsers();
}

async function loadUsers() {
  const list = document.getElementById('users-list');
  const pager = document.getElementById('users-pager');
  const summary = document.getElementById('users-summary');
  list.innerHTML = `<div class="muted">Loading…</div>`;
  pager.innerHTML = '';
  try {
    const params = new URLSearchParams({
      limit: String(state.pageSize),
      offset: String(state.offset),
      sort: state.sort,
      order: state.order,
    });
    if (state.q.trim()) params.set('q', state.q.trim());
    const page = await api(`/admin/users?${params.toString()}`);
    state.total = page.total;
    state.hasMore = page.has_more;
    summary.textContent = `${page.total.toLocaleString('en-NG')} users · showing ${page.offset + 1}–${page.offset + page.items.length}`;
    renderList(list, page.items);
    renderPager(pager);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err);
    list.innerHTML = `<div style="color:var(--color-danger)">${escape(err.message || 'Could not load users.')}</div>`;
  }
}

function renderList(host, items) {
  if (!items.length) {
    host.innerHTML = `<div class="muted">No users match.</div>`;
    return;
  }
  host.innerHTML = `
    <div style="overflow-x:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:13px;">
        <thead>
          <tr style="text-align:left;color:#aaa;font-size:11px;text-transform:uppercase;letter-spacing:0.06em;">
            <th style="padding:6px 8px;">User</th>
            <th style="padding:6px 8px;">Status</th>
            <th style="padding:6px 8px;white-space:nowrap;">Last seen</th>
            <th style="padding:6px 8px;white-space:nowrap;">Joined</th>
            <th style="padding:6px 8px;white-space:nowrap;">Actions</th>
          </tr>
        </thead>
        <tbody>
          ${items.map(renderRow).join('')}
        </tbody>
      </table>
    </div>
  `;
  host.querySelectorAll('[data-blacklist-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => onToggleBlacklist(btn));
  });
}

function renderRow(u) {
  const handle = u.username ? `@${u.username}` : '—';
  const badges = [];
  if (u.is_admin) badges.push(`<span class="status-pill bad" style="margin-left:6px;">ADMIN</span>`);
  if (u.is_blacklisted) badges.push(`<span class="status-pill bad" style="margin-left:6px;">BLACKLISTED</span>`);
  if (!u.email_verified) badges.push(`<span class="status-pill warn" style="margin-left:6px;">UNVERIFIED</span>`);
  if (u.status && u.status !== 'active') badges.push(`<span class="status-pill warn" style="margin-left:6px;">${escape(u.status.toUpperCase())}</span>`);
  const me = auth.getMe();
  const isSelf = me?.id === u.id;
  const nextState = u.is_blacklisted ? 'remove' : 'add';
  const btnLabel = u.is_blacklisted ? 'Unblacklist' : 'Blacklist';
  const btnTitle = isSelf
    ? 'You cannot blacklist yourself.'
    : (u.is_blacklisted
        ? 'Clear the blacklist flag. Future trivia joins start at 0 again.'
        : 'Flag this user; future trivia joins start at −250.');
  return `
    <tr style="border-top:1px solid #1a1432;">
      <td style="padding:8px;">
        <div style="color:#fff;">${escape(u.email)}${badges.join('')}</div>
        <div class="muted" style="font-size:11px;">${escape(handle)}</div>
      </td>
      <td style="padding:8px;color:#bbb;">${u.email_verified ? 'verified' : 'pending'}</td>
      <td style="padding:8px;white-space:nowrap;">${formatWhen(u.last_seen_at)}</td>
      <td style="padding:8px;white-space:nowrap;color:#bbb;">${formatWhen(u.created_at)}</td>
      <td style="padding:8px;white-space:nowrap;">
        <button type="button"
          class="button-secondary button-inline"
          data-blacklist-toggle="${nextState}"
          data-user-id="${escape(u.id)}"
          data-user-email="${escape(u.email)}"
          title="${escape(btnTitle)}"
          ${isSelf ? 'disabled' : ''}
          style="font-size:11px;padding:3px 8px;">
          ${btnLabel}
        </button>
      </td>
    </tr>
  `;
}

async function onToggleBlacklist(btn) {
  const action = btn.dataset.blacklistToggle; // 'add' | 'remove'
  const userId = btn.dataset.userId;
  const email = btn.dataset.userEmail;
  const verb = action === 'add' ? 'Blacklist' : 'Unblacklist';
  const tail = action === 'add'
    ? 'They will start every new live trivia at −250 points.'
    : 'Their future live trivias will start at 0 again.';
  if (!window.confirm(`${verb} ${email}? ${tail}`)) return;
  btn.disabled = true;
  try {
    await api(`/admin/users/${encodeURIComponent(userId)}/blacklist`, {
      method: action === 'add' ? 'POST' : 'DELETE',
    });
    toast.success(`${verb}ed ${email}.`);
    await loadUsers();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err);
    btn.disabled = false;
  }
}

function renderPager(host) {
  const onFirst = state.offset === 0;
  host.innerHTML = `
    <button type="button" class="button-secondary button-inline" id="prev-page" ${onFirst ? 'disabled' : ''}>← Prev</button>
    <div class="muted" style="font-size:11px;">Page ${Math.floor(state.offset / state.pageSize) + 1}</div>
    <button type="button" class="button-secondary button-inline" id="next-page" ${state.hasMore ? '' : 'disabled'}>Next →</button>
  `;
  host.querySelector('#prev-page').addEventListener('click', () => {
    state.offset = Math.max(0, state.offset - state.pageSize);
    void loadUsers();
  });
  host.querySelector('#next-page').addEventListener('click', () => {
    state.offset += state.pageSize;
    void loadUsers();
  });
}

function formatWhen(iso) {
  if (!iso) return `<span class="muted">never</span>`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return `<span class="muted">—</span>`;
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - d.getTime()) / 1000));
  let rel;
  if (diffSec < 60) rel = 'just now';
  else if (diffSec < 3600) rel = `${Math.floor(diffSec / 60)}m ago`;
  else if (diffSec < 86400) rel = `${Math.floor(diffSec / 3600)}h ago`;
  else if (diffSec < 30 * 86400) rel = `${Math.floor(diffSec / 86400)}d ago`;
  else rel = d.toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });
  const abs = d.toLocaleString('en-NG', { timeZone: 'Africa/Lagos', dateStyle: 'medium', timeStyle: 'short' });
  return `<span title="${escape(abs)}">${escape(rel)}</span>`;
}

function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}
